import { Authority } from "@/store/modules/account/account.types";

/**
 * Simple debouncer for synchronous functions
 */
let debounceTimer: undefined | ReturnType<typeof setTimeout>;

export function debouncer(
    millisecond: number,
    callbackFn: (cbArgv?: any) => any,
    ...params: any
): any {
    clearTimeout(debounceTimer as ReturnType<typeof setTimeout>);

    debounceTimer = setTimeout(() => {
        if (params) {
            return callbackFn(...params);
        }
        return callbackFn();
    }, millisecond);
}

/**
 * To set the text in titlecase
 * @param text The text to titlecase
 * @param separators Separators in array of string in case of multiple types of separator
 * @returns Titlecased text
 */
export function titlecaseText(text: string, separators: string[]): string {
    let newText: string = text;
    if (!newText || newText.length === 0 || separators.length === 0) {
        return newText;
    }

    separators.forEach(separator => {
        newText = newText.split(separator)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // uppercase the first character of the string
            .join(' ');
    });

    return newText;
}

/** Normalize ROLE_FOO / FOO / foo → FOO for comparisons across Sumari + BCM payloads. */
export function normalizeAuthorityName(authority: string): string {
    return authority.replace(/^ROLE_/i, '').toUpperCase();
}

export function authorityMatches(
    actual: string,
    expected: Authority | string
): boolean {
    return normalizeAuthorityName(actual) === normalizeAuthorityName(String(expected));
}

export function hasAuthority(
    participantProjectRoles: Array<Authority | string>,
    expected: Authority | string
): boolean {
    return participantProjectRoles.some((authority) =>
        authorityMatches(String(authority), expected)
    );
}

export function checkProjectRoles(participantProjectRoles: Authority[], allowedProjectRoles: Authority[]): boolean {
    if (participantProjectRoles.length === 0 || allowedProjectRoles.length === 0) {
        return false;
    }

    const isFound: boolean = participantProjectRoles.some((authority) =>
        allowedProjectRoles.some((allowed) => authorityMatches(authority, allowed))
    );
    return isFound;
}

/** PO, Privileged Reviewer, or Unlicensed PO — may export from Screening (SUMDEV-3492). */
export function canExportScreening(participantProjectRoles: Authority[]): boolean {
    return checkProjectRoles(participantProjectRoles, [
        Authority.ROLE_PROJECT_OWNER,
        Authority.ROLE_PRIVILEGED_REVIEWER,
        Authority.ROLE_UNLICENCED_PROJECT_OWNER,
    ]);
}

/**
 * PO or Privileged Reviewer only — may import, enter record, manage duplicates, etc.
 * UPO is always excluded, even if stale PO authority is still present.
 */
export function canMutateScreening(participantProjectRoles: Authority[]): boolean {
    if (hasAuthority(participantProjectRoles, Authority.ROLE_UNLICENCED_PROJECT_OWNER)) {
        return false;
    }
    return checkProjectRoles(participantProjectRoles, [
        Authority.ROLE_PROJECT_OWNER,
        Authority.ROLE_PRIVILEGED_REVIEWER,
    ]);
}

/**
 * PO, Privileged Reviewer, or Reviewer — may open T&A/FT and include/exclude/notes.
 * UPO is always excluded (export-only). Expired PR is demoted to Reviewer and screens via this path.
 */
export function canScreenRecords(participantProjectRoles: Authority[]): boolean {
    if (hasAuthority(participantProjectRoles, Authority.ROLE_UNLICENCED_PROJECT_OWNER)) {
        return false;
    }
    return checkProjectRoles(participantProjectRoles, [
        Authority.ROLE_PROJECT_OWNER,
        Authority.ROLE_PRIVILEGED_REVIEWER,
        Authority.ROLE_REVIEWER,
    ]);
}

export function getParticipantAuthorities(
    participantAuthorities?: Array<{ authority: Authority }> | null
): Authority[] {
    if (!participantAuthorities) {
        return [];
    }
    return participantAuthorities.map((authority) => authority.authority);
}

/** Resolve project role list from account.projectRoles (keys may be number or string). */
export function getAccountProjectRoles(
    projectRoles: { [key: number]: Array<string> } | undefined | null,
    projectId: number | string
): string[] {
    if (!projectRoles || projectId === undefined || projectId === null || projectId === '') {
        return [];
    }
    const roles =
        projectRoles[projectId as number] ??
        (projectRoles as { [key: string]: Array<string> })[String(projectId)];
    return roles ?? [];
}